import { coerceNumberProperty } from '@angular/cdk/coercion';
import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { EventEmitter, Output, Input, Component, NgModule } from '@angular/core';
import * as i1 from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { ButtonComponent, CentricButtonModule } from '@windmill/ng-windmill/button';
import { WindmillSelectComponent, WindmillSelectModule } from '@windmill/ng-windmill/select';

const DEFAULT_PAGE_SIZE = 50;
const windmillComponents = [ButtonComponent, WindmillSelectComponent];
/**
 * To use this component in a module, you can import the `WindmillPaginatorModule`:
 * @example
 * ```typescript
 * import { WindmillPaginatorModule } from '@windmill/ng-windmill/paginator';
 * ```
 *
 * To use the standalone component directly, you can import `WindmillPaginatorComponent`:
 * @example
 * ```typescript
 * import { WindmillPaginatorComponent } from '@windmill/ng-windmill/paginator';
 * ```
 */
class WindmillPaginatorComponent {
    /**
     * The text for display the number of results per page
     */
    resultsText = 'of';
    /**
     * Property uses just the basic functionalities like displaying the current page/results and the previous/next buttons.
     */
    compactVersion;
    /**
     * Property can be used to hide the page size select
     */
    hidePageSize = false;
    /**
     * The label for the page size select
     */
    resultsLabel;
    /**
     * The id attribute for the page size select
     */
    idResults;
    /**
     * The data-testid attribute for first page button from component
     */
    testFirstPageButtonId;
    /**
     * The data-testid attribute for previous page button from component
     */
    testPreviousPageButtonId;
    /**
     * The data-testid attribute for next page button from component
     */
    testNextPageButtonId;
    /**
     * The data-testid attribute for last page button from component
     */
    testLastPageButtonId;
    /**
     * The data-testid attribute for input from select
     */
    testSelectInputId;
    /**
     * The data-testid attribute for list from select
     */
    testSelectListId;
    /**
     * The description for the label controls
     */
    rangeLabelDescription;
    /**
     * The aria-label attribute for the paginator component
     */
    ariaLabel;
    /**
     * The aria-description attribute for the paginator component
     */
    ariaDescription;
    /**
     * The aria-label attribute for the first page button from component
     */
    ariaLabelFirstPageButton;
    /**
     * The aria-label attribute for the previous page button from component
     */
    ariaLabelPreviousPageButton;
    /**
     * The aria-label attribute for the next page button from component
     */
    ariaLabelNextPageButton;
    /**
     * The aria-label attribute for the last page button from component
     */
    ariaLabelLastPageButton;
    /**
     * The aria-description attribute for the page size select
     */
    ariaDescriptionResults;
    /**
     * The aria-label attribute for the page size select list
     */
    ariaLabelListResults;
    /**
     * The aria-description attribute for the page size select list
     */
    ariaDescriptionListResults;
    /**
     * The aria-description attribute for the page size select list item
     */
    ariaDescriptionListItemResults;
    /**
     * The page number-1
     */
    get pageIndex() {
        return this._pageIndex;
    }
    set pageIndex(value) {
        this._pageIndex = Math.max(coerceNumberProperty(value), 0);
    }
    /**
     * The number of the total items
     */
    get length() {
        return this._length;
    }
    set length(value) {
        this._length = coerceNumberProperty(value);
    }
    /**
     * The number of items per page
     */
    get pageSize() {
        return Number(this._pageSize);
    }
    set pageSize(value) {
        this._pageSize = Math.max(coerceNumberProperty(value), 0);
        this._updateDisplayedPageSizeOptions();
    }
    /**
     * The list of possible numbers of items per page
     */
    get pageSizeOptions() {
        return this._pageSizeOptions;
    }
    set pageSizeOptions(value) {
        this._pageSizeOptions = (value || []).map(p => coerceNumberProperty(p));
        this._updateDisplayedPageSizeOptions();
    }
    /**
     * Emits a PageProperties object when press an arrow
     */
    pageChange = new EventEmitter();
    /**
     * Emits the page size you've selected
     */
    selectedPage = new EventEmitter();
    displayedPageSizeOptions = [];
    _pageIndex = 0;
    _length = 0;
    _pageSize;
    _pageSizeOptions = [];
    _isInitialized = false;
    ngOnInit() {
        this._isInitialized = true;
        this._updateDisplayedPageSizeOptions();
    }
    goToFirstPage() {
        if (this.hasPreviousPage) {
            const previousPageIndex = this.pageIndex;
            this.pageIndex = 0;
            this._emitPageEvent(previousPageIndex);
        }
    }
    goToPreviousPage() {
        if (this.hasPreviousPage) {
            const previousPageIndex = this.pageIndex;
            this.pageIndex -= 1;
            this._emitPageEvent(previousPageIndex);
        }
    }
    goToNextPage() {
        if (this.hasNextPage) {
            const previousPageIndex = this.pageIndex;
            this.pageIndex += 1;
            this._emitPageEvent(previousPageIndex);
        }
    }
    goToLastPage() {
        if (this.hasNextPage) {
            const previousPageIndex = this.pageIndex;
            this.pageIndex = this.numberOfPages - 1;
            this._emitPageEvent(previousPageIndex);
        }
    }
    onSelectedPageSize(event) {
        this.selectedPage.emit(event);
    }
    changePageSize(pageSize) {
        const startIndex = this.pageIndex * this.pageSize;
        const previousPageIndex = this.pageIndex;
        this.pageIndex = Math.floor(startIndex / pageSize) || 0;
        this.pageSize = pageSize;
        this._emitPageEvent(previousPageIndex);
    }
    get numberOfPages() {
        if (this.pageSize) {
            return Math.ceil(this.length / this.pageSize);
        }
        return 0;
    }
    get hasPreviousPage() {
        return this.pageIndex >= 1 && this.pageSize !== 0;
    }
    get hasNextPage() {
        const maxPageIndex = this.numberOfPages - 1;
        return this.pageIndex < maxPageIndex && this.pageSize !== 0;
    }
    get rangeLabel() {
        let copyLength = this.length;
        if (copyLength === 0 || this.pageSize === 0) {
            return this.getRangeLabelInnerHtml('0', Math.ceil(copyLength));
        }
        copyLength = Math.max(copyLength, 0);
        if (this.compactVersion === 'pages') {
            return this.getRangeLabelInnerHtml(`${this.pageIndex + 1}`, Math.ceil(copyLength / this.pageSize));
        }
        const { startIndex, endIndex } = this._getStartEndIndex(copyLength);
        return this.getRangeLabelInnerHtml(`${startIndex + 1}-${endIndex}`, copyLength);
    }
    _updateDisplayedPageSizeOptions() {
        if (this._isInitialized) {
            if (!this.pageSize) {
                this._pageSize = this.pageSizeOptions.length === 0 ? DEFAULT_PAGE_SIZE : this.pageSizeOptions[0];
            }
            this.displayedPageSizeOptions = this.pageSizeOptions.slice();
            if (!this.displayedPageSizeOptions.includes(this.pageSize)) {
                this.displayedPageSizeOptions.push(this.pageSize);
            }
            this.displayedPageSizeOptions.sort((a, b) => a - b);
        }
    }
    _getStartEndIndex(copyLength) {
        let startIndex = this.pageIndex * this.pageSize;
        let endIndex = startIndex < copyLength ? Math.min(startIndex + this.pageSize, copyLength) : startIndex + this.pageSize;
        if (startIndex > copyLength) {
            startIndex = 0;
            endIndex = this.pageSize > copyLength ? copyLength : this.pageSize;
        }
        return { startIndex, endIndex };
    }
    _emitPageEvent(previousPageIndex) {
        this.pageChange.emit({
            previousPageIndex,
            pageIndex: this.pageIndex,
            pageSize: this.pageSize,
            length: this.length
        });
    }
    getRangeLabelInnerHtml(range, length) {
        return `${range} <div class="results-text-and-pages-length"><span>${this.resultsText}</span><span>${length}</span></div>`;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillPaginatorComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.10", type: WindmillPaginatorComponent, isStandalone: true, selector: "windmill-paginator", inputs: { resultsText: "resultsText", compactVersion: "compactVersion", hidePageSize: "hidePageSize", resultsLabel: "resultsLabel", idResults: "idResults", testFirstPageButtonId: "testFirstPageButtonId", testPreviousPageButtonId: "testPreviousPageButtonId", testNextPageButtonId: "testNextPageButtonId", testLastPageButtonId: "testLastPageButtonId", testSelectInputId: "testSelectInputId", testSelectListId: "testSelectListId", rangeLabelDescription: "rangeLabelDescription", ariaLabel: "ariaLabel", ariaDescription: "ariaDescription", ariaLabelFirstPageButton: "ariaLabelFirstPageButton", ariaLabelPreviousPageButton: "ariaLabelPreviousPageButton", ariaLabelNextPageButton: "ariaLabelNextPageButton", ariaLabelLastPageButton: "ariaLabelLastPageButton", ariaDescriptionResults: "ariaDescriptionResults", ariaLabelListResults: "ariaLabelListResults", ariaDescriptionListResults: "ariaDescriptionListResults", ariaDescriptionListItemResults: "ariaDescriptionListItemResults", pageIndex: "pageIndex", length: "length", pageSize: "pageSize", pageSizeOptions: "pageSizeOptions" }, outputs: { pageChange: "pageChange", selectedPage: "selectedPage" }, ngImport: i0, template: "<div class=\"paginator-controls\">\r\n  <div\r\n    role=\"group\"\r\n    class=\"paginator-navigation\"\r\n    [attr.aria-label]=\"ariaLabel\"\r\n    [attr.aria-description]=\"ariaDescription\">\r\n    @if (!compactVersion) {\r\n      <centric-button\r\n        class=\"first-page-button\"\r\n        [type]=\"'icon-ghost-greyscale'\"\r\n        [iconId]=\"'chevron-large-to-left_r'\"\r\n        [size]=\"'small'\"\r\n        [disabled]=\"!hasPreviousPage\"\r\n        [testButtonId]=\"testFirstPageButtonId\"\r\n        [ariaLabel]=\"ariaLabelFirstPageButton\"\r\n        (click)=\"goToFirstPage()\"></centric-button>\r\n    }\r\n    <centric-button\r\n      class=\"previous-page-button\"\r\n      [type]=\"'icon-ghost-greyscale'\"\r\n      [iconId]=\"'chevron-large-left_r'\"\r\n      [size]=\"'small'\"\r\n      [disabled]=\"!hasPreviousPage\"\r\n      [testButtonId]=\"testPreviousPageButtonId\"\r\n      [ariaLabel]=\"ariaLabelPreviousPageButton\"\r\n      (click)=\"goToPreviousPage()\"></centric-button>\r\n    <div\r\n      class=\"paginator-label-controls\"\r\n      aria-live=\"polite\"\r\n      aria-atomic=\"true\">\r\n      <span class=\"cdk-visually-hidden\">{{ rangeLabelDescription }}</span>\r\n      <div\r\n        class=\"paginator-status\"\r\n        [innerHTML]=\"rangeLabel\"></div>\r\n    </div>\r\n    <centric-button\r\n      class=\"next-page-button\"\r\n      [type]=\"'icon-ghost-greyscale'\"\r\n      [iconId]=\"'chevron-large-right_r'\"\r\n      [size]=\"'small'\"\r\n      [disabled]=\"!hasNextPage\"\r\n      [testButtonId]=\"testNextPageButtonId\"\r\n      [ariaLabel]=\"ariaLabelNextPageButton\"\r\n      (click)=\"goToNextPage()\"></centric-button>\r\n    @if (!compactVersion) {\r\n      <centric-button\r\n        class=\"last-page-button\"\r\n        [type]=\"'icon-ghost-greyscale'\"\r\n        [iconId]=\"'chevron-large-to-right_r'\"\r\n        [size]=\"'small'\"\r\n        [disabled]=\"!hasNextPage\"\r\n        [testButtonId]=\"testLastPageButtonId\"\r\n        [ariaLabel]=\"ariaLabelLastPageButton\"\r\n        (click)=\"goToLastPage()\"></centric-button>\r\n    }\r\n  </div>\r\n  @if (!hidePageSize) {\r\n    <div class=\"paginator-results\">\r\n      <label\r\n        class=\"paginator-label\"\r\n        [attr.for]=\"idResults\">\r\n        {{ resultsLabel }}\r\n      </label>\r\n      <windmill-select\r\n        [(ngModel)]=\"pageSize\"\r\n        [source]=\"displayedPageSizeOptions\"\r\n        [size]=\"'small'\"\r\n        [testInputId]=\"testSelectInputId\"\r\n        [testListId]=\"testSelectListId\"\r\n        [id]=\"idResults\"\r\n        [ariaDescription]=\"ariaDescriptionResults\"\r\n        [ariaLabelList]=\"ariaLabelListResults\"\r\n        [ariaDescriptionList]=\"ariaDescriptionListResults\"\r\n        [ariaDescriptionListItem]=\"ariaDescriptionListItemResults\"\r\n        (ngModelChange)=\"onSelectedPageSize($event)\"></windmill-select>\r\n    </div>\r\n  }\r\n</div>\r\n", dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "ngmodule", type: FormsModule }, { kind: "directive", type: i1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "component", type: ButtonComponent, selector: "centric-button", inputs: ["buttonType", "testButtonId", "disabled", "ariaHasPopup", "ariaControls", "ariaExpanded"] }, { kind: "component", type: WindmillSelectComponent, selector: "windmill-select", inputs: ["selectAll", "readonly"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillPaginatorComponent, decorators: [{
            type: Component,
            args: [{ selector: 'windmill-paginator', imports: [CommonModule, FormsModule, ...windmillComponents], standalone: true, template: "<div class=\"paginator-controls\">\r\n  <div\r\n    role=\"group\"\r\n    class=\"paginator-navigation\"\r\n    [attr.aria-label]=\"ariaLabel\"\r\n    [attr.aria-description]=\"ariaDescription\">\r\n    @if (!compactVersion) {\r\n      <centric-button\r\n        class=\"first-page-button\"\r\n        [type]=\"'icon-ghost-greyscale'\"\r\n        [iconId]=\"'chevron-large-to-left_r'\"\r\n        [size]=\"'small'\"\r\n        [disabled]=\"!hasPreviousPage\"\r\n        [testButtonId]=\"testFirstPageButtonId\"\r\n        [ariaLabel]=\"ariaLabelFirstPageButton\"\r\n        (click)=\"goToFirstPage()\"></centric-button>\r\n    }\r\n    <centric-button\r\n      class=\"previous-page-button\"\r\n      [type]=\"'icon-ghost-greyscale'\"\r\n      [iconId]=\"'chevron-large-left_r'\"\r\n      [size]=\"'small'\"\r\n      [disabled]=\"!hasPreviousPage\"\r\n      [testButtonId]=\"testPreviousPageButtonId\"\r\n      [ariaLabel]=\"ariaLabelPreviousPageButton\"\r\n      (click)=\"goToPreviousPage()\"></centric-button>\r\n    <div\r\n      class=\"paginator-label-controls\"\r\n      aria-live=\"polite\"\r\n      aria-atomic=\"true\">\r\n      <span class=\"cdk-visually-hidden\">{{ rangeLabelDescription }}</span>\r\n      <div\r\n        class=\"paginator-status\"\r\n        [innerHTML]=\"rangeLabel\"></div>\r\n    </div>\r\n    <centric-button\r\n      class=\"next-page-button\"\r\n      [type]=\"'icon-ghost-greyscale'\"\r\n      [iconId]=\"'chevron-large-right_r'\"\r\n      [size]=\"'small'\"\r\n      [disabled]=\"!hasNextPage\"\r\n      [testButtonId]=\"testNextPageButtonId\"\r\n      [ariaLabel]=\"ariaLabelNextPageButton\"\r\n      (click)=\"goToNextPage()\"></centric-button>\r\n    @if (!compactVersion) {\r\n      <centric-button\r\n        class=\"last-page-button\"\r\n        [type]=\"'icon-ghost-greyscale'\"\r\n        [iconId]=\"'chevron-large-to-right_r'\"\r\n        [size]=\"'small'\"\r\n        [disabled]=\"!hasNextPage\"\r\n        [testButtonId]=\"testLastPageButtonId\"\r\n        [ariaLabel]=\"ariaLabelLastPageButton\"\r\n        (click)=\"goToLastPage()\"></centric-button>\r\n    }\r\n  </div>\r\n  @if (!hidePageSize) {\r\n    <div class=\"paginator-results\">\r\n      <label\r\n        class=\"paginator-label\"\r\n        [attr.for]=\"idResults\">\r\n        {{ resultsLabel }}\r\n      </label>\r\n      <windmill-select\r\n        [(ngModel)]=\"pageSize\"\r\n        [source]=\"displayedPageSizeOptions\"\r\n        [size]=\"'small'\"\r\n        [testInputId]=\"testSelectInputId\"\r\n        [testListId]=\"testSelectListId\"\r\n        [id]=\"idResults\"\r\n        [ariaDescription]=\"ariaDescriptionResults\"\r\n        [ariaLabelList]=\"ariaLabelListResults\"\r\n        [ariaDescriptionList]=\"ariaDescriptionListResults\"\r\n        [ariaDescriptionListItem]=\"ariaDescriptionListItemResults\"\r\n        (ngModelChange)=\"onSelectedPageSize($event)\"></windmill-select>\r\n    </div>\r\n  }\r\n</div>\r\n" }]
        }], propDecorators: { resultsText: [{
                type: Input
            }], compactVersion: [{
                type: Input
            }], hidePageSize: [{
                type: Input
            }], resultsLabel: [{
                type: Input
            }], idResults: [{
                type: Input
            }], testFirstPageButtonId: [{
                type: Input
            }], testPreviousPageButtonId: [{
                type: Input
            }], testNextPageButtonId: [{
                type: Input
            }], testLastPageButtonId: [{
                type: Input
            }], testSelectInputId: [{
                type: Input
            }], testSelectListId: [{
                type: Input
            }], rangeLabelDescription: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabelFirstPageButton: [{
                type: Input
            }], ariaLabelPreviousPageButton: [{
                type: Input
            }], ariaLabelNextPageButton: [{
                type: Input
            }], ariaLabelLastPageButton: [{
                type: Input
            }], ariaDescriptionResults: [{
                type: Input
            }], ariaLabelListResults: [{
                type: Input
            }], ariaDescriptionListResults: [{
                type: Input
            }], ariaDescriptionListItemResults: [{
                type: Input
            }], pageIndex: [{
                type: Input
            }], length: [{
                type: Input
            }], pageSize: [{
                type: Input
            }], pageSizeOptions: [{
                type: Input
            }], pageChange: [{
                type: Output
            }], selectedPage: [{
                type: Output
            }] } });

const windmillModules = [CentricButtonModule, WindmillSelectModule];
const moduleComponents = [WindmillPaginatorComponent];
class WindmillPaginatorModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillPaginatorModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.10", ngImport: i0, type: WindmillPaginatorModule, imports: [CommonModule, FormsModule, CentricButtonModule, WindmillSelectModule, WindmillPaginatorComponent], exports: [WindmillPaginatorComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillPaginatorModule, imports: [CommonModule, FormsModule, windmillModules, moduleComponents] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillPaginatorModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, FormsModule, ...windmillModules, ...moduleComponents],
                    exports: [...moduleComponents]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { WindmillPaginatorComponent, WindmillPaginatorModule };
